import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Switch,
  Tabs,
  Alert,
  Badge,
  Space,
  Divider,
  Row,
  Col,
  Statistic,
  Tag,
  Progress,
  List,
  Avatar,
  Timeline,
  message,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  FileTextOutlined,
  UserOutlined,
  BookOutlined,
  BuildOutlined,
  ShopOutlined,
  BellOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
import {
  getLawsRegulationsConsolidatedData,
  getAllPremises,
} from "../../../service/api";

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface OverviewData {
  lawsRegulations: {
    total: number;
    active: number;
    expiringCertificates: number;
  };
  premises: {
    total: number;
    active: number;
    independentUnits: number;
  };
  externalVisits?: {
    total: number;
    pending: number;
    overdue: number;
  };
  tasks?: {
    total: number;
    pending: number;
    overdue: number;
  };
  policies?: {
    total: number;
    active: number;
    withExpiry: number;
  };
  staff?: {
    total: number;
    active: number;
    withExpiringTraining?: number;
  };
}

interface AlertData {
  type: string;
  severity: "Critical" | "High" | "Medium" | "Low";
  message: string;
  daysUntilExpiry?: number;
  daysOverdue?: number;
  module: string;
  id: string;
}

interface ActivityData {
  type: string;
  action: string;
  title: string;
  date: string;
  id: string;
}

interface ModuleStats {
  byType?: Array<{ _id: string; count: number }>;
  byStatus?: Array<{ _id: string; count: number }>;
  byOwnership?: Array<{ _id: string; count: number }>;
  byPeriodicity?: Array<{ _id: string; count: number }>;
  byPriority?: Array<{ _id: string; count: number }>;
  byCategory?: Array<{ _id: string; count: number }>;
  byEntity?: Array<{ _id: string; count: number }>;
}

interface ComplianceData {
  lawsRegulations?: {
    total: number;
    compliant: number;
    percentage: number;
  };
  externalVisits?: {
    total: number;
    compliant: number;
    percentage: number;
  };
  tasks?: {
    total: number;
    compliant: number;
    percentage: number;
  };
  premises?: {
    total: number;
    compliant: number;
    percentage: number;
  };
  overall?: {
    total: number;
    compliant: number;
    percentage: number;
  };
}

const LawsAndRegulationsDashboard: React.FC = () => {
  const [overview, setOverview] = useState<OverviewData>({
    lawsRegulations: { total: 0, active: 0, expiringCertificates: 0 },
    premises: { total: 0, active: 0, independentUnits: 0 },
  });
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityData[]>([]);
  const [moduleStats, setModuleStats] = useState<ModuleStats>({});
  const [compliance, setCompliance] = useState<ComplianceData>({});
  const [loading, setLoading] = useState(false);
  const [selectedModule, setSelectedModule] =
    useState<string>("laws-regulations");
  const [generatingReport, setGeneratingReport] = useState(false);

  // Fetch dashboard data with parallel API calls for faster loading
  const fetchDashboardData = async () => {
    try {
      const startTime = performance.now();

      // Instead of using the slow consolidated endpoint, 
      // fetch data from individual fast endpoints in parallel
      const [lawsResponse, premisesResponse] = await Promise.allSettled([
        // Use individual fast endpoints instead of consolidated
        getLawsRegulationsConsolidatedData(), // Keep this for now, but we'll optimize it
        getAllPremises()
      ]);
      
      const endTime = performance.now();
      
      // Process laws data from consolidated response
      if (lawsResponse.status === 'fulfilled' && lawsResponse.value.data?.success) {
        const data = lawsResponse.value.data;
        
        // Set overview data
        if (data.overview) {
          setOverview(data.overview);
        }
        
        // Set alerts data
        if (data.alerts) {
          setAlerts(data.alerts.alerts || []);
        }
        
        // Set recent activity data
        if (data.activity) {
          setRecentActivity(data.activity.activities || []);
        }
        
        // Set module stats data
        if (data.moduleStats) {
          setModuleStats(data.moduleStats["laws-regulations"] || {});
        }
        
        // Set compliance data
        if (data.compliance) {
          setCompliance(data.compliance);
        }
      }
      
      // Process premises data for additional stats
      if (premisesResponse.status === 'fulfilled' && premisesResponse.value.data) {
        const premisesData = premisesResponse.value.data;
        setOverview(prev => ({
          ...prev,
          premises: {
            ...prev.premises,
            total: premisesData.length || 0,
            active: premisesData.filter((item: any) => item.isStatus).length || 0,
            independentUnits: premisesData.filter((item: any) => item.hasIndependentUnits).length || 0
          }
        }));
      }
      
      
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      message.error("Failed to load dashboard data");
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Generate the PDF in a Web Worker instead of on the main thread —
  // react-pdf's layout pass is synchronous CPU work, and running it inline
  // (even just on click) still froze the whole page, including the sidebar.
  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      const worker = new Worker(
        new URL("./complianceReport.worker.tsx", import.meta.url)
      );

      const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        worker.onmessage = (event) => {
          if (event.data?.success) {
            resolve(event.data.buffer);
          } else {
            reject(new Error(event.data?.error || "Failed to generate PDF"));
          }
        };
        worker.onerror = (event) => {
          reject(event.error || new Error("Worker error"));
        };
        worker.postMessage({ overview, alerts, recentActivity, compliance });
      });
      worker.terminate();

      const blob = new Blob([buffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `compliance-report-${dayjs().format("YYYY-MM-DD-HH-mm")}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating compliance report:", error);
      message.error("Failed to generate compliance report");
    } finally {
      setGeneratingReport(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "Critical":
        return "#ff4d4f";
      case "High":
        return "#faad14";
      case "Medium":
        return "#1890ff";
      case "Low":
        return "#52c41a";
      default:
        return "#d9d9d9";
    }
  };

  const getModuleIcon = (module: string) => {
    switch (module) {
      case "Laws & Regulations":
        return <FileTextOutlined />;
      case "Premises":
        return <BuildOutlined />;
      case "Staff":
        return <UserOutlined />;
      case "External Visits":
        return <CalendarOutlined />;
      case "Tasks":
        return <BookOutlined />;
      case "Policies & Manuals":
        return <FileTextOutlined />;
      case "Staff Training":
        return <UserOutlined />;
      default:
        return <FileTextOutlined />;
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case "Updated":
        return <EditOutlined />;
      case "Added":
        return <PlusOutlined />;
      case "Training Completed":
        return <BookOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };


  return (
    <div className="px-2 py-4 max-w-8xl mx-auto mt-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-1">Laws & Regulations Dashboard</h2>
          <div className="text-gray-500 text-base">
            Overview of facility management compliance, alerts, and recent
            activities
          </div>
        </div>
      </div>

      {/* Overview Statistics */}
      <Row gutter={16} className="mb-6">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Laws & Regulations"
              value={overview.lawsRegulations?.total || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
            <Progress
              percent={
                overview.lawsRegulations?.total
                  ? Math.round(
                      (overview.lawsRegulations.active /
                        overview.lawsRegulations.total) *
                        100
                    )
                  : 0
              }
              size="small"
              status="active"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Laws"
              value={overview.lawsRegulations?.active || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
            <div className="text-sm text-gray-500">
              {overview.lawsRegulations?.total
                ? overview.lawsRegulations.total -
                  overview.lawsRegulations.active
                : 0}{" "}
              inactive
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Premises"
              value={overview.premises?.total || 0}
              prefix={<BuildOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
            <div className="text-sm text-gray-500">
              {overview.premises?.independentUnits || 0} with independent units
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Alerts"
              value={alerts.length}
              prefix={<BellOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
            <div className="text-sm text-gray-500">
              {alerts.filter((a) => a.severity === "Critical").length} critical
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* Alerts Section */}
        <Col span={12}>
          <Card
            title={
              <Space>
                <BellOutlined />
                Active Alerts ({alerts.length})
              </Space>
            }
            className="mb-6"
          >
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {alerts.length > 0 ? (
                <List
                  dataSource={alerts}
                  renderItem={(alert, index) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            style={{
                              backgroundColor: getSeverityColor(alert.severity),
                            }}
                            icon={<ExclamationCircleOutlined />}
                          />
                        }
                        title={
                          <Space>
                            <span>{alert.message}</span>
                            <Tag color={getSeverityColor(alert.severity)}>
                              {alert.severity}
                            </Tag>
                          </Space>
                        }
                        description={
                          <Space direction="vertical" size="small">
                            <span className="text-sm text-gray-500">
                              Module: {alert.module}
                            </span>
                            {alert.daysUntilExpiry && (
                              <span className="text-sm text-orange-500">
                                Expires in {alert.daysUntilExpiry} days
                              </span>
                            )}
                            {alert.daysOverdue && (
                              <span className="text-sm text-red-500">
                                Overdue by {alert.daysOverdue} days
                              </span>
                            )}
                          </Space>
                        }
                      />
                      <Button type="link" size="small">
                        View Details
                      </Button>
                    </List.Item>
                  )}
                />
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No active alerts
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* Recent Activity */}
        <Col span={12}>
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                Recent Activity
              </Space>
            }
            className="mb-6"
          >
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Timeline>
                {recentActivity.map((activity, index) => (
                  <Timeline.Item
                    key={index}
                    dot={getActionIcon(activity.action)}
                    color={activity.action === "Updated" ? "blue" : activity.action === "Added" ? "green" : "orange"}
                  >
                    <div>
                      <div className="font-medium">{activity.title}</div>
                      <div className="text-sm text-gray-500">
                        {activity.action} • {dayjs(activity.date).format('MMM DD, YYYY HH:mm')}
                      </div>
                      <Tag icon={getModuleIcon(activity.type)}>
                        {activity.type}
                      </Tag>
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Module Statistics */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Laws & Regulations Statistics" className="mb-6">
            <Row gutter={16}>
              <Col span={12}>
                <div className="mb-4">
                  <h4 className="font-medium mb-2">By Category</h4>
                  {moduleStats.byType?.map((stat, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center mb-2"
                    >
                      <span className="text-sm">{stat._id || 'Unknown'}</span>
                      <Badge
                        count={stat.count}
                        style={{ backgroundColor: "#1890ff" }}
                      />
                    </div>
                  ))}
                  {(!moduleStats.byType || moduleStats.byType.length === 0) && (
                    <div className="text-sm text-gray-500">No data available</div>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <div className="mb-4">
                  <h4 className="font-medium mb-2">By Status</h4>
                  {moduleStats.byStatus?.map((stat, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center mb-2"
                    >
                      <span className="text-sm">{stat._id || 'Unknown'}</span>
                      <Badge
                        count={stat.count}
                        style={{
                          backgroundColor:
                            stat._id === "Active" ? "#52c41a" : "#d9d9d9",
                        }}
                      />
                    </div>
                  ))}
                  {(!moduleStats.byStatus || moduleStats.byStatus.length === 0) && (
                    <div className="text-sm text-gray-500">No data available</div>
                  )}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Quick Actions" className="mb-6">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                block
                onClick={() =>
                  (window.location.href = "/laws-regulations-policies")
                }
              >
                Add New Law/Regulation
              </Button>
              <Button
                icon={<BuildOutlined />}
                block
                onClick={() => (window.location.href = "/premises-information")}
              >
                Manage Premises
              </Button>
              <Button
                icon={<UserOutlined />}
                block
                onClick={() => (window.location.href = "/staff-management")}
              >
                Manage Staff
              </Button>
              <Button
                icon={<FileTextOutlined />}
                block
                loading={generatingReport}
                onClick={handleGenerateReport}
              >
                {generatingReport ? 'Generating PDF...' : 'Generate Compliance Report'}
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Compliance Overview */}
      <Card title="Compliance Overview" className="mb-6">
        <Row gutter={16}>
          <Col span={8}>
            <div className="text-center">
              <Progress
                type="circle"
                percent={compliance.overall?.percentage || 0}
                format={(percent) => `${percent}%`}
                strokeColor="#52c41a"
              />
              <div className="mt-2 font-medium">Overall Compliance</div>
            </div>
          </Col>
          <Col span={8}>
            <div className="text-center">
              <Progress
                type="circle"
                percent={compliance.lawsRegulations?.percentage || 0}
                format={(percent) => `${percent}%`}
                strokeColor="#faad14"
              />
              <div className="mt-2 font-medium">Laws & Regulations</div>
            </div>
          </Col>
          <Col span={8}>
            <div className="text-center">
              <Progress
                type="circle"
                percent={compliance.premises?.percentage || 0}
                format={(percent) => `${percent}%`}
                strokeColor="#1890ff"
              />
              <div className="mt-2 font-medium">Premises</div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Additional Statistics */}
      {overview.externalVisits && (
        <Row gutter={16} className="mb-6">
          <Col span={8}>
            <Card>
              <Statistic
                title="External Visits"
                value={overview.externalVisits.total}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
              <div className="text-sm text-gray-500">
                {overview.externalVisits.pending} pending,{" "}
                {overview.externalVisits.overdue} overdue
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Tasks"
                value={overview.tasks?.total || 0}
                prefix={<BookOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
              <div className="text-sm text-gray-500">
                {overview.tasks?.pending || 0} pending,{" "}
                {overview.tasks?.overdue || 0} overdue
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Staff"
                value={overview.staff?.total || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
              <div className="text-sm text-gray-500">
                {overview.staff?.active || 0} active
                {overview.staff?.withExpiringTraining && (
                  <span>, {overview.staff.withExpiringTraining} with expiring training</span>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Upcoming Deadlines */}
      <Card title="Upcoming Deadlines" className="mb-6">
        <Table
          columns={[
            {
              title: "Item",
              dataIndex: "item",
              key: "item",
            },
            {
              title: "Type",
              dataIndex: "type",
              key: "type",
              render: (type: string) => (
                <Tag
                  color={
                    type === "Certificate"
                      ? "blue"
                      : type === "Visit"
                      ? "green"
                      : "orange"
                  }
                >
                  {type}
                </Tag>
              ),
            },
            {
              title: "Due Date",
              dataIndex: "dueDate",
              key: "dueDate",
              render: (date: string) => dayjs(date).format("MMM DD, YYYY"),
            },
            {
              title: "Days Remaining",
              dataIndex: "daysRemaining",
              key: "daysRemaining",
              render: (days: number) => (
                <Tag
                  color={days <= 7 ? "red" : days <= 30 ? "orange" : "green"}
                >
                  {days} days
                </Tag>
              ),
            },
            {
              title: "Status",
              key: "status",
              render: (text: string, record: any) => (
                <Badge
                  status={
                    record.daysRemaining <= 7
                      ? "error"
                      : record.daysRemaining <= 30
                      ? "warning"
                      : "success"
                  }
                  text={
                    record.daysRemaining <= 7
                      ? "Urgent"
                      : record.daysRemaining <= 30
                      ? "Warning"
                      : "On Track"
                  }
                />
              ),
            },
          ]}
          dataSource={alerts
            .filter(
              (alert) => alert.daysUntilExpiry && alert.daysUntilExpiry > 0
            )
            .slice(0, 5)
            .map((alert, index) => ({
              key: index.toString(),
              item: alert.message,
              type: alert.type.includes("Certificate")
                ? "Certificate"
                : alert.type.includes("Visit")
                ? "Visit"
                : "Other",
              dueDate: dayjs()
                .add(alert.daysUntilExpiry || 0, "day")
                .format("YYYY-MM-DD"),
              daysRemaining: alert.daysUntilExpiry || 0,
            }))}
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default LawsAndRegulationsDashboard;